import { FC } from 'react';
import { t } from '@lingui/macro';

import { Button, Flex, Paragraph, Text } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';

import { BRIGHT_HORIZONS_CREDITS_DONT_APPLY } from '../../../callouts/CalloutBadge/CalloutBadge.constants';

import usePriceDisplayInfo, { PriceDisplayProps } from './usePriceDisplayInfo';

export type VariantType = 'minimal' | 'maximal' | 'cta-desktop' | 'cta-mobile';

type Props = PriceDisplayProps & {
  variant?: VariantType;
  onPriceClick?: () => void;
};

const SimplifiedPriceColumn: FC<Props> = ({
  variant = 'minimal',
  onPriceClick,
  ...props
}: Props) => {
  const { formattedPrice, beforePriceText, afterPriceText, showBrightHorizonsCalloutBadge } =
    usePriceDisplayInfo(props);

  const actualBeforePriceText =
    beforePriceText && variant === 'maximal' ? t`From` : beforePriceText;

  const textProps = {
    size: variant === 'cta-mobile' ? ('100' as const) : ('200' as const),
    textColor: 'secondary' as const,
    fontWeight: 'regular' as const,
  };

  const visiblePhrase = (
    <>
      {actualBeforePriceText && <Text {...textProps}>{`${actualBeforePriceText} `}</Text>}
      <Text
        fontWeight="semibold"
        size={onPriceClick ? '200' : '300'}
        textColor="primary"
        sx={onPriceClick ? { textDecoration: 'underline' } : undefined}
      >
        {formattedPrice}
      </Text>
      <Text {...textProps}>{` ${afterPriceText}`}</Text>
    </>
  );

  const priceContent = onPriceClick ? (
    <Button
      variant="link"
      padding="0x"
      data-testid="price-column-button"
      aria-label={t`${actualBeforePriceText} ${formattedPrice} ${afterPriceText}, see all rates`}
      onPress={onPriceClick}
      sx={{ color: 'text.primary', textDecoration: 'unset' }}
    >
      <span aria-hidden="true">{visiblePhrase}</span>
    </Button>
  ) : (
    visiblePhrase
  );

  return (
    <Flex
      sx={{ marginLeft: variant !== 'minimal' ? 'unset' : ['unset', 'auto'] }}
      gap="2x"
      flexWrap="wrap"
    >
      <Paragraph marginBottom="0x" data-testid="price-highlight">
        {priceContent}
      </Paragraph>
      {showBrightHorizonsCalloutBadge && <Badge {...BRIGHT_HORIZONS_CREDITS_DONT_APPLY()} />}
    </Flex>
  );
};

export default SimplifiedPriceColumn;
